// =====================================================================
// DIALOG DROPDOWN FIX — kill the Frappe `tooltip-content` overlay
// =====================================================================
// In Frappe v16 dialogs, every awesomplete Link result <li> contains a
// hidden `<span class="tooltip-content">FULL VALUE</span>`. Bootstrap /
// Frappe shows this as a TOOLTIP on hover/focus that floats OVER other
// dropdown items — producing the "value overlapping the next field"
// visual the user reported ("Company Letterhead - Grey" mid-dropdown).
//
// Fix at the source: when an awesomplete dropdown opens inside ANY
// modal, remove the `title` attributes and hide every tooltip-content
// span so no preview-bubble can ever appear over the list.
//
// Lightweight: event-driven only. No body-wide MutationObserver, because
// reports and child-table grids can churn DOM heavily while users drag or
// resize columns.
// =====================================================================

let mounted = false;

// Selectors we scan for awesomplete items.
// Includes: every modal, modal-dialog, grid row editor (.form-in-grid /
// .grid-row-open) — and as a final safety net, every awesomplete <li>
// anywhere on the page. The grid editor is NOT a .modal in Frappe v16
// so it was previously slipping past this strip.
const ITEM_SEL =
	".modal .awesomplete > ul li, " +
	".modal .awesomplete > ul li *, " +
	".modal-dialog .awesomplete > ul li, " +
	".modal-dialog .awesomplete > ul li *, " +
	".form-in-grid .awesomplete > ul li, " +
	".form-in-grid .awesomplete > ul li *, " +
	".grid-row-open .awesomplete > ul li, " +
	".grid-row-open .awesomplete > ul li *, " +
	".awesomplete > ul li .tooltip-content, " +
	".awesomplete > ul li[title], " +
	".awesomplete > ul li[data-original-title]";

function strip(scope) {
	const root = scope || document;
	let items;
	try { items = root.querySelectorAll(ITEM_SEL); } catch (e) { return; }
	items.forEach((el) => {
		try {
			// Remove the title attribute that drives the Bootstrap tooltip
			if (el.hasAttribute && el.hasAttribute("title")) el.removeAttribute("title");
			if (el.hasAttribute && el.hasAttribute("data-original-title"))
				el.removeAttribute("data-original-title");
			// Inline the tooltip-content span so it can't float over rows
			if (el.classList && el.classList.contains("tooltip-content")) {
				el.style.setProperty("display", "none", "important");
				el.style.setProperty("visibility", "hidden", "important");
				el.style.setProperty("position", "absolute", "important");
				el.style.setProperty("left", "-9999px", "important");
				el.style.setProperty("top", "-9999px", "important");
				el.style.setProperty("width", "0", "important");
				el.style.setProperty("height", "0", "important");
				el.style.setProperty("pointer-events", "none", "important");
			}
		} catch (e) {}
	});
	// Also kill any active Bootstrap tooltip that already escaped to <body>.
	// While ANY awesomplete dropdown is open, suppress every floating tip.
	const anyOpen = !!document.querySelector(".awesomplete > ul:not([hidden])");
	const modalOpen = !!document.querySelector(".modal .awesomplete > ul:not([hidden]), .modal-content .awesomplete > ul:not([hidden])");
	document.body?.classList.toggle("e16-awesomplete-open", anyOpen);
	document.body?.classList.toggle("e16-modal-awesomplete-open", modalOpen);
	document.querySelectorAll("body > .tooltip, body > .popover").forEach((tip) => {
		try {
			if (anyOpen) {
				tip.style.setProperty("display", "none", "important");
				tip.style.setProperty("visibility", "hidden", "important");
				return;
			}
			const content = (tip.textContent || "").trim();
			if (content && content.length < 80) {
				tip.style.setProperty("display", "none", "important");
			}
		} catch (e) {}
	});
}

let scheduled = false;
let pendingScope = null;
function schedule(scope) {
	pendingScope = scope || pendingScope || document;
	if (scheduled) return;
	scheduled = true;
	requestAnimationFrame(() => {
		scheduled = false;
		const scope = pendingScope || document;
		pendingScope = null;
		try { strip(scope); } catch (e) {}
	});
}

export const dialogDropdownFix = {
	mount() {
		if (mounted) return;
		mounted = true;

		const kick = () => schedule(document);
		if (document.readyState === "loading") {
			document.addEventListener("DOMContentLoaded", kick, { once: true });
		} else {
			kick();
		}

		try {
			const $ = window.jQuery || window.$;
			if ($) $(document).on("shown.bs.modal", (e) => schedule(e.target || document));
		} catch (e) {}

		document.addEventListener(
			"awesomplete-open",
			(e) => schedule(e.target?.closest?.(".modal, .form-in-grid, .grid-row-open") || document),
			true
		);

		// Re-strip whenever any awesomplete-bearing field gains focus, in
		// ANY scope (modal, dialog, grid row editor, or even base form).
		document.addEventListener(
			"focusin",
			(e) => {
				if (!e.target || !e.target.closest) return;
				if (
					e.target.closest(".modal .awesomplete") ||
					e.target.closest(".form-in-grid .awesomplete") ||
					e.target.closest(".grid-row-open .awesomplete") ||
					e.target.closest(".awesomplete")
				) schedule(e.target.closest(".modal, .form-in-grid, .grid-row-open") || document);
			},
			true
		);

		// Re-strip on every keystroke inside an awesomplete input — that's
		// when Frappe re-renders <li> nodes with fresh title attrs.
		document.addEventListener(
			"input",
			(e) => {
				if (!e.target || !e.target.closest) return;
				if (e.target.closest(".awesomplete")) {
					schedule(e.target.closest(".modal, .form-in-grid, .grid-row-open") || document);
				}
			},
			true
		);
	},
};
